import {
  GeometryType,
  TaskGeometry,
  ShapeType,
  MarkerShape,
} from '../../types';
import { LineType, LineStyle, FillType } from '../../types/geometry';
import {
  createDefaultLineGeometry,
  createDefaultShapeGeometry,
  createDefaultMarkerGeometry,
} from '../../types/geometry/geometryFactories';
import { DEFAULT_MAP_CENTER } from '../../constants';
import i18n from '../../config/i18n';
import { generateDefaultGeometryName } from '../../components/map/GeometriesList/utils';
import { calculateActiveParentIndex } from '../../utils/geometry/lineHelpers';

/**
 * Drawing Tools State
 *
 * Manages the state of the drawing toolbox including:
 * - Active drawing tool (Marker, Line, Shape, Selection)
 * - Current geometries being edited (supports multiple shapes per task)
 * - Active geometry ID (which geometry is currently being edited/drawn)
 * - Selection state (selected geometry and vertex for Selection mode)
 * - Drawing mode state
 * - Undo/Redo history
 * - Toolbox UI state (position, minimized)
 *
 * @since 3.0.0 - Changed to support multiple geometries per task
 */
export interface DrawingToolsState {
  // Active tool selection
  activeTool: GeometryType;

  // Drawing state (multi-geometry support)
  isDrawing: boolean;
  currentGeometries: TaskGeometry[]; // Array of geometries (changed from single currentGeometry)
  activeGeometryId: string | null; // ID of geometry being edited/drawn
  activeParentIndex: number | null; // Index of point for line branching (next point connects to this point)

  // Selection state (Selection mode)
  selectedGeometryId: string | null; // ID of currently selected geometry (Selection mode only)
  selectedVertexIndex: number | null; // Index of selected vertex (Line geometry in Selection mode)
  selectedClickPosition: { x: number; y: number } | null; // Screen coordinates of click that selected geometry (for delete button positioning)
  selectedGeographicPosition: { lng: number; lat: number } | null; // Geographic coordinates of click that selected geometry (for MapLibre Marker positioning)

  // Multi-select state (GeometriesList bulk operations)
  selectedGeometryIds: string[]; // Array of selected geometry IDs for bulk operations

  // Marker visibility state
  hideOtherMarkers: boolean;

  // Hover state (which geometry is currently hovered)
  hoveredGeometryId: string | null;

  // Drag state (which geometry is currently being dragged)
  draggingGeometryId: string | null;

  // Point drag state (indices of points being dragged - for hiding number markers)
  // Used to control visibility of PointMarkers during drag via React state (not DOM manipulation)
  draggingPointIndices: number[] | null;

  // Whole-line drag flag (explicit flag to avoid race conditions with draggingPointIndices)
  // Only set by useGeometryWholeDrag, never by useLinePointDrag
  // Prevents all markers from disappearing during vertex drag on mobile
  isWholeDragActive: boolean;

  // Double-click protection (prevents adding new marker when double-clicking existing marker)
  // Issue: HTML marker overlay events don't propagate to MapLibre canvas
  // Solution: Marker sets this flag on dblclick, map checks it before adding new marker
  // CRITICAL: Map timer must use getState() to read flag (not closure - see useMapEventHandlers.ts)
  isDoubleClickInProgress: boolean;

  // Toolbox UI state
  toolboxPosition: { x: number; y: number };
  toolboxMinimized: boolean;
  isPanelOpen: boolean; // SettingsPanel (Level 2) visibility state
  activeSettingPanel: string | null; // ControlsPanel (Level 3) active panel
  isCustomColorPickerOpen: boolean; // CustomColorPanel (Level 4) visibility state
  activeColorTarget:
    | 'markerColor'
    | 'lineColor'
    | 'shapeFillColor'
    | 'shapeBorderColor'
    | null; // Which color is being edited

  // Navigation panel state (unified Navigation/Selection tool)
  navigationPanel: {
    isExpanded: boolean; // Panel expansion state
    isSelectionSwapped: boolean; // Icon swap state (Selection becomes main button)
  };

  // Undo/Redo history (snapshots of entire geometry array)
  history: TaskGeometry[][];
  historyIndex: number;
  maxHistorySteps: number;
}

/**
 * Drawing Tools Actions
 *
 * Actions for managing drawing tools, geometry editing, selection, history, and multi-geometry support
 *
 * @since 3.0.0 - Updated actions for multiple geometries per task
 */
export interface DrawingToolsActions {
  // Tool selection
  setActiveTool: (tool: GeometryType) => void;

  // Multi-geometry actions
  addGeometry: (geometry: TaskGeometry) => void;
  updateGeometry: (id: string, updates: Partial<TaskGeometry>) => void;
  removeGeometry: (id: string) => void;
  setActiveGeometry: (id: string | null) => void;
  clearGeometries: () => void;
  loadGeometries: (geometries: TaskGeometry[]) => void;

  // Bulk operations (GeometriesList)
  toggleGeometrySelection: (id: string) => void;
  selectAllGeometries: () => void;
  clearGeometrySelection: () => void;
  bulkDeleteGeometries: (ids: string[]) => void;
  renameGeometry: (id: string, newName: string) => void;

  // Line branching actions
  setActiveParentIndex: (index: number | null) => void;

  // Selection actions (Selection mode)
  selectGeometry: (
    id: string | null,
    clickPosition?: { x: number; y: number },
    geographicPosition?: { lng: number; lat: number }
  ) => void;
  deselectGeometry: () => void;
  selectVertex: (index: number | null) => void;
  deselectVertex: () => void;
  clearSelection: () => void;

  // Marker visibility actions
  toggleHideOtherMarkers: () => void;
  setHideOtherMarkers: (value: boolean) => void;

  // Hover actions
  setHoveredGeometry: (id: string | null) => void;

  // Drag actions
  setDraggingGeometry: (id: string | null) => void;
  setDraggingPointIndices: (indices: number[] | null) => void;
  setWholeDragActive: (active: boolean) => void;

  // Double-click protection action
  setDoubleClickProtection: (value: boolean) => void;

  // History actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;

  // Toolbox UI actions
  setToolboxPosition: (position: { x: number; y: number }) => void;
  toggleToolboxMinimized: () => void;
  resetToolboxPosition: () => void;

  // SettingsPanel (Level 2) actions
  togglePanel: () => void;
  closePanel: () => void;

  // ControlsPanel (Level 3) actions
  setActiveSettingPanel: (panel: string | null) => void;
  closeSettingPanel: () => void;

  // CustomColorPanel (Level 4) actions
  openCustomColorPicker: (
    target: 'markerColor' | 'lineColor' | 'shapeFillColor' | 'shapeBorderColor'
  ) => void;
  toggleCustomColorPicker: (
    target: 'markerColor' | 'lineColor' | 'shapeFillColor' | 'shapeBorderColor'
  ) => void;
  closeCustomColorPicker: () => void;

  // Navigation panel actions (unified Navigation/Selection tool)
  toggleNavigationPanel: () => void;
  closeNavigationPanel: () => void;
  swapNavigationIcons: () => void;
  resetNavigationPanel: () => void;

  // Mutual exclusion - close all tool panels at once
  closeAllToolPanels: () => void;
}

export type DrawingToolsSlice = DrawingToolsState & DrawingToolsActions;

// Default toolbox positions
const getDefaultPosition = (): { x: number; y: number } => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) {
    // Mobile: bottom center
    return {
      x: 20,
      y: typeof window !== 'undefined' ? window.innerHeight - 400 : 400,
    };
  }
  // Desktop: left side, vertical center
  return { x: 20, y: 200 };
};

/**
 * Create Drawing Tools Slice
 *
 * Zustand slice for managing drawing tools state with Immer middleware support
 */
export const createDrawingToolsSlice = (
  set: (fn: (state: DrawingToolsSlice) => void) => void,
  get: () => DrawingToolsSlice,
  _store: unknown
): DrawingToolsSlice => ({
  // Initial state
  activeTool: GeometryType.None, // Start in Free Roam mode (navigation without drawing)
  isDrawing: false,
  currentGeometries: [], // Changed from currentGeometry to array
  activeGeometryId: null, // New: ID of active geometry
  activeParentIndex: null, // No parent index by default (line branching)
  selectedGeometryId: null, // No geometry selected by default (Selection mode)
  selectedVertexIndex: null, // No vertex selected by default (Selection mode)
  selectedClickPosition: null, // No click position by default (Selection mode)
  selectedGeographicPosition: null, // No geographic position by default (Selection mode)
  selectedGeometryIds: [], // No geometries selected for bulk operations by default
  hideOtherMarkers: false, // Default: markers visible (auto-hidden by uiSlice when form opens)
  hoveredGeometryId: null, // No geometry hovered by default
  draggingGeometryId: null, // No geometry being dragged by default
  draggingPointIndices: null, // No points being dragged by default (used to hide number markers)
  isWholeDragActive: false, // Explicit flag for whole-line drag (not inferred)
  isDoubleClickInProgress: false, // No double-click in progress by default
  toolboxPosition: getDefaultPosition(),
  toolboxMinimized: false,
  isPanelOpen: false, // SettingsPanel hidden by default
  activeSettingPanel: null, // ControlsPanel hidden by default
  isCustomColorPickerOpen: false, // CustomColorPanel hidden by default
  activeColorTarget: null, // No color being edited
  navigationPanel: {
    isExpanded: false, // Panel collapsed by default
    isSelectionSwapped: false, // Navigation is main button by default
  },
  history: [], // Changed to array of arrays
  historyIndex: -1,
  maxHistorySteps: 50,

  // Tool selection
  setActiveTool: (tool) =>
    set((state) => {
      const previousTool = state.activeTool;
      state.activeTool = tool;

      // Reset activeParentIndex when switching tools (line branching)
      state.activeParentIndex = null;

      // Close Level 3 and Level 4 when switching tools
      state.activeSettingPanel = null;
      state.isCustomColorPickerOpen = false;
      state.activeColorTarget = null;

      // ========================================
      // FIX: Close navigation panel when switching to drawing tools
      // ========================================
      // Panel should only be open for Navigation/Selection modes
      if (tool !== GeometryType.None && tool !== GeometryType.Selection) {
        state.navigationPanel.isExpanded = false;
      }

      // ========================================
      // FIX: Clear selection when switching AWAY from Selection mode
      // ========================================
      if (
        previousTool === GeometryType.Selection &&
        tool !== GeometryType.Selection
      ) {
        state.selectedGeometryId = null;
        state.selectedVertexIndex = null;
        state.selectedClickPosition = null;
        state.selectedGeographicPosition = null;
      }

      // ========================================
      // SPECIAL CASE: Free Roam Mode (None) or Selection Mode
      // ========================================
      if (tool === GeometryType.None || tool === GeometryType.Selection) {
        // Exit drawing mode completely
        state.isDrawing = false;
        state.activeGeometryId = null;
        // Don't create any geometry, just navigation/selection mode
        return;
      }

      // ========================================
      // FIX: Exit drawing mode when switching between tools
      // ========================================
      if (previousTool !== tool && previousTool !== GeometryType.None) {
        state.isDrawing = false;
        state.activeGeometryId = null;
      }

      // ========================================
      // DRAWING TOOLS: Marker, Line, Shape
      // ========================================
      // Panel will be opened manually in MapToolbox component

      // ========================================
      // Marker Tool → Create placeholder (not rendered, only for UI settings)
      // ========================================
      // Create a placeholder marker for UI synchronization (settings panels).
      // Placeholder is NOT rendered on map (filtered in PreviewableMarker).
      // First map click will convert placeholder to real marker (in useMarkerDrawing).
      if (tool === GeometryType.Marker) {
        // Get marker preferences from store (cross-slice access)
        const fullState = get() as unknown as {
          toolPreferences?: { marker: unknown };
        };
        const markerPrefs = fullState.toolPreferences?.marker as
          | {
              shape?: MarkerShape;
              color?: string;
              icon?: string;
            }
          | undefined;

        // Use first existing geometry's center or default (Warsaw)
        const centerCoords =
          state.currentGeometries[0]?.calculatedCenter || DEFAULT_MAP_CENTER;

        // Create placeholder marker with user preferences
        const newGeometry = createDefaultMarkerGeometry(centerCoords, {
          shape: markerPrefs?.shape,
          color: markerPrefs?.color,
          icon: markerPrefs?.icon,
        });

        // Mark as placeholder - will NOT render on map, only for settings UI
        newGeometry.isPlaceholder = true;

        // Auto-generate name if not provided or empty
        if (!newGeometry.name || newGeometry.name.trim() === '') {
          newGeometry.name = generateDefaultGeometryName(
            newGeometry.type,
            state.currentGeometries,
            i18n.t
          );
        }

        // Add placeholder to geometries array
        state.currentGeometries.push(newGeometry);
        state.activeGeometryId = newGeometry.id;
        state.isDrawing = true;

        // Add to history
        if (state.historyIndex < state.history.length - 1) {
          state.history = state.history.slice(0, state.historyIndex + 1);
        }
        state.history.push([...state.currentGeometries]);
        state.historyIndex++;
        if (state.history.length > state.maxHistorySteps) {
          state.history.shift();
          state.historyIndex--;
        }
        return;
      }

      // CRITICAL: When switching to Line or Shape tool, create NEW geometry if none active
      // This allows ADDING geometry without replacing existing marker
      const hasActiveGeometry = state.activeGeometryId !== null;
      const needsNewGeometry =
        !hasActiveGeometry &&
        (tool === GeometryType.Line || tool === GeometryType.Shape);

      if (needsNewGeometry) {
        let newGeometry;
        // Use first existing geometry's center or default
        const centerCoords =
          state.currentGeometries[0]?.calculatedCenter || DEFAULT_MAP_CENTER;

        // Get tool preferences from store (cross-slice access)
        const fullState = get() as unknown as {
          toolPreferences?: { marker: unknown; line: unknown; shape: unknown };
        };
        const toolPrefs = fullState.toolPreferences;

        if (tool === GeometryType.Line) {
          // Create empty line geometry with user preferences
          const linePrefs = toolPrefs?.line as
            | {
                type?: LineType;
                style?: LineStyle;
                width?: number;
                color?: string;
              }
            | undefined;

          newGeometry = createDefaultLineGeometry([], centerCoords, {
            type: linePrefs?.type,
            style: linePrefs?.style,
            width: linePrefs?.width,
            color: linePrefs?.color,
          });
        } else if (tool === GeometryType.Shape) {
          // Create empty shape geometry with user preferences (like Line tool)
          const shapePrefs = toolPrefs?.shape as
            | {
                type?: ShapeType;
                fill?: FillType;
                fillColor?: string;
                borderStyle?: LineStyle;
                borderWidth?: number;
                borderColor?: string;
              }
            | undefined;

          newGeometry = createDefaultShapeGeometry(
            shapePrefs?.type ?? ShapeType.Rectangle, // ✅ FIXED: Rectangle is default (first in list)
            [], // ✅ FIXED: Empty array (like Line tool - shape not visible until first click)
            centerCoords,
            {
              fill: shapePrefs?.fill,
              fillColor: shapePrefs?.fillColor,
              borderStyle: shapePrefs?.borderStyle,
              borderWidth: shapePrefs?.borderWidth,
              borderColor: shapePrefs?.fillColor, // Border = Fill (same color)
            }
          );
        }

        if (newGeometry) {
          // Auto-generate name if not provided or empty
          if (!newGeometry.name || newGeometry.name.trim() === '') {
            newGeometry.name = generateDefaultGeometryName(
              newGeometry.type,
              state.currentGeometries,
              i18n.t
            );
          }

          // Add new geometry to array (ADDS, doesn't replace)
          state.currentGeometries.push(newGeometry);
          state.activeGeometryId = newGeometry.id;
          state.isDrawing = true;

          // Add to history
          if (state.historyIndex < state.history.length - 1) {
            state.history = state.history.slice(0, state.historyIndex + 1);
          }
          state.history.push([...state.currentGeometries]);
          state.historyIndex++;
          if (state.history.length > state.maxHistorySteps) {
            state.history.shift();
            state.historyIndex--;
          }
        }
      }
    }),

  // Multi-geometry actions
  addGeometry: (geometry) =>
    set((state) => {
      // Auto-generate name if not provided or empty
      if (!geometry.name || geometry.name.trim() === '') {
        geometry.name = generateDefaultGeometryName(
          geometry.type,
          state.currentGeometries,
          i18n.t
        );
      }

      // Add geometry to array
      state.currentGeometries.push(geometry);

      // Set as active geometry
      state.activeGeometryId = geometry.id;

      // Mark as drawing if it's a new geometry
      state.isDrawing = true;

      // Truncate future history
      if (state.historyIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.historyIndex + 1);
      }

      // Add snapshot to history
      state.history.push([...state.currentGeometries]);
      state.historyIndex++;

      // Enforce max history steps
      if (state.history.length > state.maxHistorySteps) {
        state.history.shift();
        state.historyIndex--;
      }
    }),

  updateGeometry: (id, updates) =>
    set((state) => {
      const index = state.currentGeometries.findIndex((g) => g.id === id);
      if (index === -1) return;

      // Update geometry (Immer handles mutation)
      const geometry = state.currentGeometries[index];
      if (geometry) {
        Object.assign(geometry, updates);
      }

      // Truncate future history
      if (state.historyIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.historyIndex + 1);
      }

      // Add snapshot to history
      state.history.push([...state.currentGeometries]);
      state.historyIndex++;

      // Enforce max history steps
      if (state.history.length > state.maxHistorySteps) {
        state.history.shift();
        state.historyIndex--;
      }
    }),

  removeGeometry: (id) =>
    set((state) => {
      state.currentGeometries = state.currentGeometries.filter(
        (g) => g.id !== id
      );

      // Reset active geometry if it was removed
      if (state.activeGeometryId === id) {
        state.activeGeometryId = null;
        state.isDrawing = false;
      }

      // Truncate future history
      if (state.historyIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.historyIndex + 1);
      }

      // Add snapshot to history
      state.history.push([...state.currentGeometries]);
      state.historyIndex++;

      // Enforce max history steps
      if (state.history.length > state.maxHistorySteps) {
        state.history.shift();
        state.historyIndex--;
      }
    }),

  setActiveGeometry: (id) =>
    set((state) => {
      // Clear dragging state when switching geometries
      // This ensures number markers are visible when editing a different line
      // BUT: Don't clear if drag is in progress (draggingGeometryId is set)
      // This fixes the mobile bug where marker reappears during vertex drag
      if (!state.draggingGeometryId) {
        state.draggingPointIndices = null;
      }

      state.activeGeometryId = id;

      // Automatically switch tool if geometry is of different type
      if (id) {
        const geometry = state.currentGeometries.find((g) => g.id === id);
        if (geometry) {
          state.activeTool = geometry.type;
          state.isDrawing = true;

          // BUG FIX 2: Set activeParentIndex for Line geometry when entering edit mode
          // This highlights the "last added vertex" (green) so user knows where next point will connect
          if (geometry.type === GeometryType.Line && geometry.linePoints) {
            state.activeParentIndex = calculateActiveParentIndex(
              geometry.linePoints
            );
          }
        }
      } else {
        // Only set isDrawing = false if NOT in Line tool idle state
        // Line tool should stay "active" (isDrawing = true) even after finishing a line
        // This allows Line tool to remain ready for drawing the next line
        if (state.activeTool !== GeometryType.Line) {
          state.isDrawing = false;
        }
        // If Line tool: keep isDrawing = true (idle state, ready for next line)
      }
    }),

  clearGeometries: () =>
    set((state) => {
      state.currentGeometries = [];
      state.activeGeometryId = null;
      state.isDrawing = false;
      state.history = [];
      state.historyIndex = -1;
    }),

  loadGeometries: (geometries) =>
    set((state) => {
      state.currentGeometries = geometries;
      // NOTE: Caller is responsible for setting isDrawing and activeGeometryId
      // This follows Single Responsibility Principle - loadGeometries ONLY manages geometries and history

      // Reset history with initial snapshot
      state.history = [geometries];
      state.historyIndex = 0;
    }),

  // Bulk operations (GeometriesList)
  toggleGeometrySelection: (id) =>
    set((state) => {
      const index = state.selectedGeometryIds.indexOf(id);
      if (index > -1) {
        // Already selected - remove from selection
        state.selectedGeometryIds.splice(index, 1);
      } else {
        // Not selected - add to selection
        state.selectedGeometryIds.push(id);
      }
    }),

  selectAllGeometries: () =>
    set((state) => {
      state.selectedGeometryIds = state.currentGeometries.map((g) => g.id);
    }),

  clearGeometrySelection: () =>
    set((state) => {
      state.selectedGeometryIds = [];
    }),

  bulkDeleteGeometries: (ids) =>
    set((state) => {
      // Remove all selected geometries
      state.currentGeometries = state.currentGeometries.filter(
        (g) => !ids.includes(g.id)
      );

      // Reset active geometry if it was removed
      if (state.activeGeometryId && ids.includes(state.activeGeometryId)) {
        state.activeGeometryId = null;
        state.isDrawing = false;
      }

      // Clear selection
      state.selectedGeometryIds = [];

      // Truncate future history
      if (state.historyIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.historyIndex + 1);
      }

      // Add snapshot to history
      state.history.push([...state.currentGeometries]);
      state.historyIndex++;

      // Enforce max history steps
      if (state.history.length > state.maxHistorySteps) {
        state.history.shift();
        state.historyIndex--;
      }
    }),

  renameGeometry: (id, newName) =>
    set((state) => {
      const geometry = state.currentGeometries.find((g) => g.id === id);
      if (geometry) {
        geometry.name = newName;

        // Truncate future history
        if (state.historyIndex < state.history.length - 1) {
          state.history = state.history.slice(0, state.historyIndex + 1);
        }

        // Add snapshot to history
        state.history.push([...state.currentGeometries]);
        state.historyIndex++;

        // Enforce max history steps
        if (state.history.length > state.maxHistorySteps) {
          state.history.shift();
          state.historyIndex--;
        }
      }
    }),

  // Selection actions (Selection mode)
  selectGeometry: (id, clickPosition, geographicPosition) =>
    set((state) => {
      state.selectedGeometryId = id;
      // Always clear vertex selection when selecting geometry
      // This ensures clicking line (not vertex) deselects individual vertices
      state.selectedVertexIndex = null;
      // Store click position for delete button positioning (screen coords)
      state.selectedClickPosition = clickPosition || null;
      // Store geographic position for MapLibre Marker positioning
      state.selectedGeographicPosition = geographicPosition || null;
    }),

  deselectGeometry: () =>
    set((state) => {
      state.selectedGeometryId = null;
      state.selectedVertexIndex = null;
      state.selectedClickPosition = null;
      state.selectedGeographicPosition = null;
    }),

  selectVertex: (index) =>
    set((state) => {
      state.selectedVertexIndex = index;
    }),

  deselectVertex: () =>
    set((state) => {
      state.selectedVertexIndex = null;
    }),

  clearSelection: () =>
    set((state) => {
      state.selectedGeometryId = null;
      state.selectedVertexIndex = null;
      state.selectedClickPosition = null;
      state.selectedGeographicPosition = null;
    }),

  // Line branching actions
  setActiveParentIndex: (index) =>
    set((state) => {
      state.activeParentIndex = index;
    }),

  // Marker visibility actions
  toggleHideOtherMarkers: () =>
    set((state) => {
      state.hideOtherMarkers = !state.hideOtherMarkers;
    }),

  setHideOtherMarkers: (value: boolean) =>
    set((state) => {
      state.hideOtherMarkers = value;
    }),

  // Hover actions
  setHoveredGeometry: (id) =>
    set((state) => {
      state.hoveredGeometryId = id;
    }),

  // Drag actions
  setDraggingGeometry: (id) =>
    set((state) => {
      state.draggingGeometryId = id;
    }),

  setDraggingPointIndices: (indices) =>
    set((state) => {
      state.draggingPointIndices = indices;
    }),

  setWholeDragActive: (active) =>
    set((state) => {
      state.isWholeDragActive = active;
    }),

  // Double-click protection action
  setDoubleClickProtection: (value) =>
    set((state) => {
      state.isDoubleClickInProgress = value;
    }),

  // History actions
  undo: () =>
    set((state) => {
      if (state.historyIndex > 0) {
        state.historyIndex--;
        const historySnapshot = state.history[state.historyIndex];
        if (historySnapshot) {
          state.currentGeometries = [...historySnapshot];

          // Check if active geometry still exists after undo
          if (
            !state.currentGeometries.find(
              (g) => g.id === state.activeGeometryId
            )
          ) {
            state.activeGeometryId = state.currentGeometries[0]?.id || null;
          }

          // BUG FIX: Recalculate activeParentIndex after undo
          // When undoing line drawing (e.g., removing last point), the active vertex
          // should be updated to highlight the new last point (green)
          if (state.activeGeometryId) {
            const activeGeometry = state.currentGeometries.find(
              (g) => g.id === state.activeGeometryId
            );
            if (
              activeGeometry?.type === GeometryType.Line &&
              activeGeometry.linePoints
            ) {
              state.activeParentIndex = calculateActiveParentIndex(
                activeGeometry.linePoints
              );
            } else {
              // Not a line or no points - reset activeParentIndex
              state.activeParentIndex = null;
            }
          } else {
            // No active geometry - reset activeParentIndex
            state.activeParentIndex = null;
          }
        }
      }
    }),

  redo: () =>
    set((state) => {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex++;
        const historySnapshot = state.history[state.historyIndex];
        if (historySnapshot) {
          state.currentGeometries = [...historySnapshot];

          // Check if active geometry still exists after redo
          if (
            !state.currentGeometries.find(
              (g) => g.id === state.activeGeometryId
            )
          ) {
            state.activeGeometryId = state.currentGeometries[0]?.id || null;
          }

          // BUG FIX: Recalculate activeParentIndex after redo
          // When redoing line drawing (e.g., re-adding removed point), the active vertex
          // should be updated to highlight the restored last point (green)
          if (state.activeGeometryId) {
            const activeGeometry = state.currentGeometries.find(
              (g) => g.id === state.activeGeometryId
            );
            if (
              activeGeometry?.type === GeometryType.Line &&
              activeGeometry.linePoints
            ) {
              state.activeParentIndex = calculateActiveParentIndex(
                activeGeometry.linePoints
              );
            } else {
              // Not a line or no points - reset activeParentIndex
              state.activeParentIndex = null;
            }
          } else {
            // No active geometry - reset activeParentIndex
            state.activeParentIndex = null;
          }
        }
      }
    }),

  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },

  canRedo: () => {
    const state = get();
    return state.historyIndex < state.history.length - 1;
  },

  clearHistory: () =>
    set((state) => {
      state.history = [];
      state.historyIndex = -1;
    }),

  // Toolbox UI actions
  setToolboxPosition: (position) =>
    set((state) => {
      state.toolboxPosition = position;
    }),

  toggleToolboxMinimized: () =>
    set((state) => {
      state.toolboxMinimized = !state.toolboxMinimized;
    }),

  resetToolboxPosition: () =>
    set((state) => {
      state.toolboxPosition = getDefaultPosition();
    }),

  // SettingsPanel (Level 2) actions
  togglePanel: () =>
    set((state) => {
      state.isPanelOpen = !state.isPanelOpen;

      // Close Level 3 and Level 4 when closing Level 2
      if (!state.isPanelOpen) {
        state.activeSettingPanel = null;
        state.isCustomColorPickerOpen = false;
        state.activeColorTarget = null;
      }

      // MUTUAL EXCLUSION: Close navigation panel when drawing tool panel opens
      if (state.isPanelOpen) {
        state.navigationPanel.isExpanded = false;

        // Close layer control panel when drawing tool panel opens
        if (
          'ui' in state &&
          typeof state.ui === 'object' &&
          state.ui &&
          'mapControls' in state.ui
        ) {
          (
            state.ui as { mapControls: { isLayersExpanded: boolean } }
          ).mapControls.isLayersExpanded = false;
        }
      }
    }),

  closePanel: () =>
    set((state) => {
      state.isPanelOpen = false;
      // Close Level 3 when closing Level 2
      state.activeSettingPanel = null;
      // Close Level 4 when closing Level 2
      state.isCustomColorPickerOpen = false;
      state.activeColorTarget = null;
    }),

  // ControlsPanel (Level 3) actions
  setActiveSettingPanel: (panel) =>
    set((state) => {
      state.activeSettingPanel = panel;
      // Close Level 4 when switching Level 3 panels
      state.isCustomColorPickerOpen = false;
      state.activeColorTarget = null;
    }),

  closeSettingPanel: () =>
    set((state) => {
      state.activeSettingPanel = null;
      // Close Level 4 when closing Level 3
      state.isCustomColorPickerOpen = false;
      state.activeColorTarget = null;
    }),

  // CustomColorPanel (Level 4) actions
  openCustomColorPicker: (target) =>
    set((state) => {
      // On mobile, close Level 3 panel - CustomColorPanel replaces it
      // On desktop, keep Level 3 open - CustomColorPanel appears beside it
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth < 1024;
      if (isMobile) {
        state.activeSettingPanel = null;
      }
      state.isCustomColorPickerOpen = true;
      state.activeColorTarget = target;
    }),

  toggleCustomColorPicker: (target) =>
    set((state) => {
      if (state.isCustomColorPickerOpen) {
        state.isCustomColorPickerOpen = false;
        state.activeColorTarget = null;
      } else {
        // On mobile, close Level 3 panel - CustomColorPanel replaces it
        // On desktop, keep Level 3 open - CustomColorPanel appears beside it
        const isMobile =
          typeof window !== 'undefined' && window.innerWidth < 1024;
        if (isMobile) {
          state.activeSettingPanel = null;
        }
        state.isCustomColorPickerOpen = true;
        state.activeColorTarget = target;
      }
    }),

  closeCustomColorPicker: () =>
    set((state) => {
      state.isCustomColorPickerOpen = false;
      state.activeColorTarget = null;
    }),

  // Navigation panel actions (unified Navigation/Selection tool)
  toggleNavigationPanel: () =>
    set((state) => {
      state.navigationPanel.isExpanded = !state.navigationPanel.isExpanded;

      // MUTUAL EXCLUSION: Close drawing tool panels when navigation panel opens
      if (state.navigationPanel.isExpanded) {
        state.isPanelOpen = false; // Close SettingsPanel (Level 2)
        state.activeSettingPanel = null; // Close ControlsPanel (Level 3)
        state.isCustomColorPickerOpen = false; // Close CustomColorPanel (Level 4)
        state.activeColorTarget = null;

        // Close layer control panel when navigation panel opens
        if (
          'ui' in state &&
          typeof state.ui === 'object' &&
          state.ui &&
          'mapControls' in state.ui
        ) {
          (
            state.ui as { mapControls: { isLayersExpanded: boolean } }
          ).mapControls.isLayersExpanded = false;
        }
      }
    }),

  closeNavigationPanel: () =>
    set((state) => {
      state.navigationPanel.isExpanded = false;
    }),

  swapNavigationIcons: () =>
    set((state) => {
      state.navigationPanel.isSelectionSwapped =
        !state.navigationPanel.isSelectionSwapped;
    }),

  resetNavigationPanel: () =>
    set((state) => {
      state.navigationPanel.isExpanded = false;
      state.navigationPanel.isSelectionSwapped = false;
    }),

  // Mutual exclusion - close all tool panels at once
  closeAllToolPanels: () =>
    set((state) => {
      // Close navigation panel
      state.navigationPanel.isExpanded = false;

      // Close drawing tool panels (all levels)
      state.isPanelOpen = false; // Level 2
      state.activeSettingPanel = null; // Level 3
      state.isCustomColorPickerOpen = false; // Level 4
      state.activeColorTarget = null;
    }),
});
